import React from "react";
import { observer } from "mobx-react";

// material-icons has no LinkedIn brand glyph, so inline the logo. Without
// this, the "LinkedIn DM" recommendation rendered an empty icon box.
const LinkedInGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V9.93H5.55v8.41h2.79zM6.95 8.69a1.62 1.62 0 1 0 0-3.24 1.62 1.62 0 0 0 0 3.24zm11.39 9.65v-4.61c0-2.46-1.31-3.6-3.07-3.6-1.17 0-1.95.64-2.27 1.25h-.04v-1.05H10.1v8.41h2.78v-4.16c0-1.1.21-2.16 1.57-2.16 1.34 0 1.36 1.25 1.36 2.23v4.09h2.92z"/>
  </svg>
);

// Pure-derive component. No LLM calls. Reads cached research fields and
// computes the best outbound channel + a 1-line rationale.
//
// Logic (priority order):
//   1) Active LinkedIn posts in last 30d → LinkedIn DM
//   2) Recent talk / podcast / interview → Reference event in DM or email
//   3) Active hiring in seller's domain → Email about hiring
//   4) Quiet on LinkedIn (no posts in 90d) but high profile → Email
//   5) Default → Multi-channel (email + LinkedIn touch)

const daysAgo = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
};

const computeChannel = ({ researchData, userInsights }) => {
  const posts = userInsights?.deeper_context?.notable_posts || [];
  const mostRecentPostDays = posts.length
    ? Math.min(...posts.map(p => daysAgo(p.date)).filter(v => v != null))
    : null;

  const deepDive = researchData?.deep_dive_intel || {};
  const speaking = Array.isArray(deepDive?.speaking_signals) ? deepDive.speaking_signals : [];
  const hiring = Array.isArray(deepDive?.hiring_signals) ? deepDive.hiring_signals : [];

  // Rule 1: Active LinkedIn (post in last 30 days)
  if (mostRecentPostDays != null && mostRecentPostDays <= 30) {
    return {
      channel: "LinkedIn DM",
      icon: "linkedin",
      rationale: `Posted ${mostRecentPostDays} ${mostRecentPostDays === 1 ? "day" : "days"} ago — they engage here. Comment first, DM second.`,
      tier: "primary"
    };
  }

  // Rule 2: Recent speaking / podcast
  if (speaking.length > 0) {
    const ev = typeof speaking[0] === "string" ? speaking[0] : (speaking[0]?.event_or_publication || speaking[0]?.topic);
    if (ev) {
      return {
        channel: "Reference the event",
        icon: "campaign",
        rationale: `Recently appeared at "${ev}" — use it as the opener regardless of channel.`,
        tier: "primary"
      };
    }
  }

  // Rule 3: Active hiring (seller-domain relevant)
  if (hiring.length > 0) {
    const h = typeof hiring[0] === "string" ? hiring[0] : (hiring[0]?.role || hiring[0]?.title);
    if (h) {
      return {
        channel: "Email — they're scaling",
        icon: "mail",
        rationale: `Active hiring (${h}) signals priority budget. Email about the role; LinkedIn as follow-up.`,
        tier: "primary"
      };
    }
  }

  // Rule 4: Quiet on LinkedIn (no posts in 90d)
  if (mostRecentPostDays != null && mostRecentPostDays > 90) {
    return {
      channel: "Email",
      icon: "mail",
      rationale: `No LinkedIn activity in ${mostRecentPostDays} days. They don't engage here — go straight to inbox.`,
      tier: "primary"
    };
  }

  // Rule 5: No post data at all
  if (mostRecentPostDays == null) {
    return {
      channel: "Email-first, LinkedIn assist",
      icon: "mail",
      rationale: "No public LinkedIn activity surfaced. Lead with email; use LinkedIn for the follow-up touch.",
      tier: "default"
    };
  }

  // Rule 6: Moderately active (31-90 days)
  return {
    channel: "Multi-channel (email + LinkedIn)",
    icon: "alt_route",
    rationale: `Last LinkedIn post ${mostRecentPostDays} days ago — moderate engagement. Email primary, LinkedIn DM as follow-up.`,
    tier: "default"
  };
};

const BestChannel = observer(({ researchData, userInsights }) => {
  if (!researchData) return null;
  const rec = computeChannel({ researchData, userInsights });
  if (!rec) return null;

  return (
    <div className={`drChannel drChannel--${rec.tier}`}>
      <div className="drChannel__iconWrap">
        {rec.icon === "linkedin" ? (
          <span className="drChannel__icon drChannel__icon--brand" aria-hidden="true">
            <LinkedInGlyph />
          </span>
        ) : (
          <i className="material-icons-outlined drChannel__icon">{rec.icon}</i>
        )}
      </div>
      <div className="drChannel__body">
        <div className="drChannel__label">Best Channel</div>
        <div className="drChannel__title">{rec.channel}</div>
        <div className="drChannel__rationale">{rec.rationale}</div>
      </div>
    </div>
  );
});

export default BestChannel;
